// src/pages/MartyrsTeam.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@/components/PageHero';
import { MemorialCard } from '@/components/MemorialCard';
import { Button } from '@/components/ui/button';
import { martyrsTeam } from '@/data/mock/martyrsTeam';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Users, 
  Award, 
  Flame, 
  Sparkles,
  Quote,
  Calendar,
  Flag,
  BookOpen,
  Filter,
  Search,
  X
} from 'lucide-react';

const MartyrsTeam = ({ embedded = false }: { embedded?: boolean }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Get unique roles for filtering
  const roles = ['all', ...Array.from(new Set(martyrsTeam.map(m => m.role.en)))];

  // Filter martyrs
  const filteredMartyrs = martyrsTeam.filter(martyr => {
    const matchesRole = selectedRole === 'all' || martyr.role.en === selectedRole;
    const matchesSearch = searchQuery === '' || 
      martyr.name.en.toLowerCase().includes(searchQuery.toLowerCase()) ||
      martyr.name.ar.includes(searchQuery);
    return matchesRole && matchesSearch;
  });

  // Statistics
  const stats = [
    {
      icon: Heart,
      value: martyrsTeam.length.toString(),
      label: { en: 'Heroes Remembered', ar: 'أبطال نتذكرهم' },
      color: 'from-red-500 to-pink-500'
    },
    {
      icon: Users,
      value: martyrsTeam.filter(m => m.role.en.includes('Coach')).length.toString(),
      label: { en: 'Coaches', ar: 'مدربون' },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Award,
      value: martyrsTeam.filter(m => m.role.en.includes('Director') || m.role.en.includes('Manager')).length.toString(),
      label: { en: 'Leaders', ar: 'قادة' },
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Sparkles,
      value: '∞',
      label: { en: 'Legacy Lives On', ar: 'إرث حي' },
      color: 'from-yellow-500 to-orange-500'
    },
  ];

  // Memorial quotes
  const memorialQuotes = [
    {
      text: {
        en: 'They gave their lives building dreams for the next generation. Their sacrifice will never be forgotten.',
        ar: 'بذلوا حياتهم في بناء أحلام الجيل القادم. لن ننسى تضحياتهم أبداً.'
      },
      author: { en: 'Champions Academy', ar: 'أكاديمية تشامبيونز' }
    },
    {
      text: {
        en: 'In sports, they found purpose. In teaching, they found meaning. In service, they found immortality.',
        ar: 'في الرياضة وجدوا الهدف. في التعليم وجدوا المعنى. في الخدمة وجدوا الخلود.'
      },
      author: { en: 'Their Legacy', ar: 'إرثهم' }
    },
  ];

  return (
    <div className={embedded ? '' : 'min-h-screen'}>
      {/* Enhanced Page Hero */}
      {!embedded && (
        <PageHero
          title={t('martyrs.teamTitle')}
          subtitle={isRTL
            ? 'نكرم ذكرى أبطالنا الذين كرسوا حياتهم للرياضة والمجتمع - إرثهم يعيش في كل طفل ألهموه'
            : 'Honoring our heroes who dedicated their lives to sports and community - their legacy lives in every child they inspired'
          }
          breadcrumbs={[
            { label: t('common.home'), path: '/' },
            { label: t('nav.martyrsTeam') }
          ]}
        />
      )}

      {/* Memorial Banner */}
      <section className="py-6 bg-gradient-to-r from-red-500/10 via-background to-red-500/10 border-y border-red-500/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center gap-3 text-center"
          >
            <Flame className="h-6 w-6 text-red-500" />
            <p className="text-sm font-semibold text-foreground">
              {isRTL
                ? 'ذكراهم شعلة لا تنطفئ في قلوبنا'
                : 'Their memory is an eternal flame in our hearts'
              }
            </p>
            <Flame className="h-6 w-6 text-red-500" />
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            {/* Memorial Icon */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", duration: 0.8 }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-red-500 to-pink-500 p-1 mb-8 shadow-2xl shadow-red-500/30"
            >
              <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                <Heart className="h-12 w-12 text-red-500 animate-pulse" />
              </div>
            </motion.div>

            {/* Text */}
            <p className="text-xl text-muted-foreground leading-relaxed mb-8">
              {isRTL
                ? 'نكرم ذكرى أعضاء فريقنا الذين كرسوا حياتهم للرياضة والمجتمع. إرثهم يعيش من خلال الآلاف من الشباب الذين ألهموهم.'
                : 'We honor the memory of our team members who dedicated their lives to sports and community. Their legacy lives on through the thousands of young lives they inspired.'}
            </p>

            {/* Forever Badge */}
            <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-red-500/10 border-2 border-red-500/30 shadow-lg">
              <Heart className="h-6 w-6 text-red-500" />
              <span className="text-red-500 font-bold text-lg">{t('martyrs.forever')}</span>
              <Heart className="h-6 w-6 text-red-500" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Award className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'إحصائيات' : 'Statistics'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'أبطال لن ننساهم' : 'Heroes We Will Never Forget'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -8 }}
                className="p-8 rounded-3xl bg-card border-2 border-border hover:border-red-500/30 shadow-xl hover:shadow-2xl transition-all duration-500 text-center"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.color} bg-opacity-10 mb-4`}>
                  <stat.icon className="h-8 w-8 text-primary" />
                </div>
                <div className="text-5xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground font-medium">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Memorial Quotes */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto space-y-8">
            {memorialQuotes.map((quote, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="p-10 rounded-3xl bg-card border-2 border-border shadow-xl relative"
              >
                <Quote className="h-16 w-16 text-red-500/20 absolute top-6 left-6" />
                <div className="relative pl-20">
                  <p className="text-2xl text-foreground font-medium leading-relaxed mb-6 italic">
                    "{quote.text[isRTL ? 'ar' : 'en']}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center">
                      <Heart className="h-6 w-6 text-red-500" />
                    </div>
                    <div>
                      <div className="font-bold text-foreground">
                        {quote.author[isRTL ? 'ar' : 'en']}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="py-12 bg-surface-1 top-20 z-40 border-b border-border shadow-lg">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
                <input
                  type="text"
                  placeholder={isRTL ? 'ابحث عن اسم...' : 'Search by name...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full ${isRTL ? 'pr-12 pl-12' : 'pl-12 pr-12'} py-3 rounded-full bg-card border-2 border-border focus:border-primary/50 outline-none transition-all`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-4' : 'right-4'} w-6 h-6 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-all`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Role Filter */}
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="px-6 py-3 rounded-full bg-card border-2 border-border focus:border-primary/50 outline-none cursor-pointer transition-all font-medium"
                >
                  <option value="all">{isRTL ? 'الكل' : 'All Roles'}</option>
                  {roles.slice(1).map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results Count */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 text-center"
            >
              <span className="text-sm text-muted-foreground">
                {isRTL 
                  ? `عرض ${filteredMartyrs.length} من ${martyrsTeam.length}`
                  : `Showing ${filteredMartyrs.length} of ${martyrsTeam.length}`
                }
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Memorial Cards Grid */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          {filteredMartyrs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredMartyrs.map((member, idx) => (
                <MemorialCard
                  key={member.id}
                  name={member.name[isRTL ? 'ar' : 'en']}
                  role={member.role[isRTL ? 'ar' : 'en']}
                  years={member.years}
                  image={member.image}
                  bio={member.bio?.[isRTL ? 'ar' : 'en']}
                  achievements={member.achievements?.[isRTL ? 'ar' : 'en']}
                  quote={member.quote?.[isRTL ? 'ar' : 'en']}
                  dateOfPassing={member.dateOfPassing}
                  delay={idx * 0.1}
                />
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-lg text-muted-foreground">
                {isRTL ? 'لم يتم العثور على نتائج' : 'No results found'}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Legacy Message */}
      <section className="py-20 bg-gradient-to-r from-red-500/10 via-pink-500/10 to-red-500/10 relative overflow-hidden">
        {/* Animated Background */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-red-500/30 via-pink-500/20 to-transparent rounded-full blur-3xl"
        />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            {/* Icon */}
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-red-500 to-pink-500 p-1 mb-8 shadow-2xl"
            >
              <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                <Flame className="h-12 w-12 text-red-500" />
              </div>
            </motion.div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 gradient-text">
              {isRTL ? 'إرثهم يعيش' : 'Their Legacy Lives On'}
            </h2>

            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              {isRTL
                ? 'في كل طفل يلعب، في كل حلم يتحقق، في كل بطولة نفوز بها - يعيش إرث أبطالنا. لن ننساهم أبداً.'
                : 'In every child who plays, in every dream achieved, in every championship we win - the legacy of our heroes lives on. We will never forget them.'
              }
            </p>

            {/* Memorial Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="px-12 py-7 text-lg rounded-full btn-glow">
                <BookOpen className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'كتاب الذكريات' : 'Book of Memories'}
              </Button>

              <Button size="lg" variant="outline" className="px-12 py-7 text-lg rounded-full border-2">
                <Heart className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'شارك ذكرى' : 'Share a Memory'}
              </Button>
            </div>

            {/* Eternal Flame Symbol */}
            <motion.div
              animate={{
                opacity: [0.5, 1, 0.5],
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="mt-16 pt-16 border-t border-border"
            >
              <div className="flex items-center justify-center gap-4">
                <div className="w-16 h-0.5 bg-gradient-to-r from-transparent to-red-500" />
                <Flame className="h-12 w-12 text-red-500" />
                <div className="w-16 h-0.5 bg-gradient-to-l from-transparent to-red-500" />
              </div>
              <p className="text-sm text-muted-foreground mt-4 font-medium">
                {isRTL ? 'شعلة خالدة في الذاكرة' : 'An Eternal Flame in Memory'}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MartyrsTeam;
